import { Inject, Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import { IdleActivityService } from './idle-activity.service';
import { IdleActivityConfigInjectionToken } from './idle-activity-config-injection.token';
import { IdleUtils } from './idle-utils';
import { IdleActivityConfig } from './idle-activity-config';
import { ActiveAuthenticationStateService } from '@auth-n/state/active-authentication.state-service';
import { AuthenticationService } from '@auth-n/authentication.service';

@Injectable({ providedIn: 'root' })
export class IdleStateChangeHandler {
  constructor(
    @Inject(IdleActivityConfigInjectionToken) private config: IdleActivityConfig,
    private utils: IdleUtils,
    private idleActivityService: IdleActivityService,
    private authenticationService: AuthenticationService,
    private authenticationState: ActiveAuthenticationStateService
  ) {}

  /**
   * watch for a change in authentication status to reset idle-monitoring
   */
  configureIdleMonitoring() {
    this.authenticationState.activeIdentity$
      .pipe(
        tap((identity) => this.utils.trace('IdleStateChangeHandler: active identity changed to ', identity)),
        map((identity) => !!identity),
        distinctUntilChanged(),
        skipWhile((authenticated) => !authenticated),
        switchMap((authenticated) => (authenticated ? this.idleTimeout$() : this.stopMonitoring$()))
      )
      .subscribe(() => {
        this.utils.trace('IdleStateChangeHandler: idle time-out; signing-out.');
        this.authenticationService.signOutAndRedirect();
      });

    this.utils.trace('IdleStateChangeHandler: configured.');
  }

  private idleTimeout$(): Observable<boolean> {
    this.idleActivityService.startMonitoring();
    return this.idleActivityService.idleTimeout$.pipe(filter(Boolean), take(1));
  }

  private stopMonitoring$(): Observable<never> {
    this.idleActivityService.stopMonitoring();
    return EMPTY;
  }
}
